import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Mail, BellRing } from "lucide-react";
import { linkitApi, formatApiErrorDetail } from "../api";

export default function SettingsPanel() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    linkitApi.get("/settings").then((r) => setS(r.data)).catch(() => toast.error("Could not load settings"));
  }, []);

  if (!s) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-white/40" /></div>;

  const upd = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await linkitApi.put("/settings", {
        display_name: s.display_name,
        notification_email: s.notification_email,
        notify_leads_enabled: s.notify_leads_enabled,
      });
      setS(data);
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-bold text-white">Settings</h2>
        <p className="text-xs text-white/50">Manage account and lead notifications.</p>
      </div>

      <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
        <p className="mb-3 text-sm font-bold text-white/80">Account</p>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs text-white/60">Email (login)</label>
            <input value={s.email || ""} disabled className="w-full cursor-not-allowed rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/60" />
          </div>
          <div>
            <label className="mb-1 block text-xs text-white/60">Display name</label>
            <input
              data-testid="settings-display-name"
              value={s.display_name || ""}
              onChange={(e) => upd("display_name", e.target.value)}
              className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none focus:border-indigo-400"
            />
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
        <div className="mb-3 flex items-center gap-2">
          <BellRing size={16} className="text-indigo-300" />
          <p className="text-sm font-bold text-white/80">Lead notifications</p>
        </div>
        <p className="mb-3 text-xs text-white/50">
          When someone submits your Contact form, we&apos;ll email you the details so you never miss a lead.
        </p>
        <label className="mb-3 flex cursor-pointer items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] p-3">
          <div className="flex items-center gap-3">
            <Mail size={16} className="text-white/70" />
            <div>
              <p className="text-sm font-semibold text-white">Email me new leads</p>
              <p className="text-[11px] text-white/40">Turn off to stop notifications (leads still saved).</p>
            </div>
          </div>
          <input
            data-testid="settings-notify-toggle"
            type="checkbox"
            checked={!!s.notify_leads_enabled}
            onChange={(e) => upd("notify_leads_enabled", e.target.checked)}
            className="h-5 w-5 accent-indigo-500"
          />
        </label>
        <div>
          <label className="mb-1 block text-xs text-white/60">Notification email</label>
          <input
            data-testid="settings-notify-email"
            type="email"
            value={s.notification_email || ""}
            onChange={(e) => upd("notification_email", e.target.value)}
            placeholder="you@example.com"
            className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none focus:border-indigo-400"
          />
          <p className="mt-1 text-[11px] text-white/40">Defaults to your login email. Change it to send lead alerts to a different inbox.</p>
        </div>
      </section>

      <button
        onClick={save}
        disabled={saving}
        data-testid="settings-save"
        className="inline-flex items-center gap-2 rounded-full bg-indigo-500 px-5 py-2.5 text-sm font-bold text-white hover:bg-indigo-400 disabled:opacity-60"
      >
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save settings
      </button>
    </div>
  );
}
